import {ApolloQueryResult} from 'apollo-client';
import React, {FC, memo} from 'react';

import {
  Organization,
  useTeamOrganizationQuery,
} from '../../../generated/graphql';
import {
  Override,
  TeamOrganization,
  TeamPageEntity,
} from '../../../graphql/teamQuery';
import {useViewerMember} from '../../../pages/Team/utils';
import {useViewer} from '../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../util/admin';
import {TeamMembersTable} from './TeamMembersTable';
import {TeamMembersTableHeader} from './TeamMembersTableHeader';
import {useGetRoleOptions} from './useGetRoleOptions';
import {TeamMemberRole} from './utils';

interface TeamMembersProps {
  entity: TeamPageEntity;
  refetch?: () => Promise<ApolloQueryResult<any>>;
}

export const TeamMembers: FC<TeamMembersProps> = memo(({entity, refetch}) => {
  const viewer = useViewer();
  // team access can be explicitly disabled for some sys admins
  const isAdminModeActive =
    useAdminModeActive() && !viewer?.instanceAdminTeamAccessDisabled;
  const viewerMember = useViewerMember(entity, viewer);

  const entityName = entity?.name;
  const {
    data,
    loading: isTeamOrgQueryLoading,
    refetch: orgRefetch,
  } = useTeamOrganizationQuery({
    variables: {entityName},
  });

  const isViewerAdmin =
    isAdminModeActive || viewerMember?.role === TeamMemberRole.ADMIN;

  const {roleOptions, isLoading: isRoleOptionsLoading} = useGetRoleOptions(
    entity.name
  );

  const organization = data?.entity?.organization;

  const typedOrganization = organization as Override<
    typeof organization,
    TeamOrganization
  >;

  const handleReload = () => {
    if (refetch) {
      refetch();
    }
    orgRefetch();
  };

  return (
    <>
      {!isRoleOptionsLoading && !isTeamOrgQueryLoading && (
        <>
          <TeamMembersTableHeader
            entity={entity}
            refetch={handleReload}
            organization={organization as Organization}
            isViewerAdmin={isViewerAdmin}
          />
          <TeamMembersTable
            roleOptions={roleOptions}
            isViewerAdmin={isViewerAdmin}
            entity={entity}
            organization={typedOrganization}
            refetch={handleReload}
            viewer={viewer}
          />
        </>
      )}
    </>
  );
});
